import React from "react";
import { Modal, Button, Tag } from "antd";
import {
  PictureOutlined,
  PlayCircleOutlined,
  AppstoreOutlined,
  BgColorsOutlined,
  CalendarOutlined,
  HomeOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import "./viewHomeBanner.css";

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })
    : "—";

const capitalize = (s) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const HomeBannerViewModal = ({ visible, bannerData, onClose }) => {
  const d = bannerData || {};
  const td = d.type_data || {};
  const hasVideo = !!d.home_videos;
  const hasImage = !!d.home_image;
  const hasThumbnail = !!d.thumbnail;

  return (
    <Modal
      title={
        <div className="view-hb-modal-header-content">
          <PictureOutlined className="view-hb-modal-header-icon" style={{ color: "white" }} />
          <span style={{ color: "white" }}>BANNER DETAILS</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={780}
      className="view-hb-modal-override"
      styles={{
        header: {
          background: "#070C18",
          borderRadius: "12px 12px 0 0",
          padding: "16px 24px",
          borderBottom: "none",
        },
        body: { padding: "20px 24px", background: "#f5f6fa" },
      }}
    >

      {/* ── General Info ── */}
      <div className="view-hb-card">
        <h3 className="view-hb-card-header">General Information</h3>
        <div className="view-hb-info-grid">

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><HomeOutlined /> Home Type</span>
            <span className="view-hb-badge view-hb-badge-blue">{capitalize(d.home_type)}</span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><AppstoreOutlined /> Banner Type</span>
            <span className="view-hb-badge view-hb-badge-purple">{capitalize(d.type)}</span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><BgColorsOutlined /> Theme</span>
            <span className={`view-hb-badge ${d.theme === "dark" ? "view-hb-badge-dark" : "view-hb-badge-light"}`}>
              {capitalize(d.theme)}
            </span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><OrderedListOutlined /> Position</span>
            <span className="view-hb-badge view-hb-badge-gray">{d.position ?? "—"}</span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label">Status</span>
            <span className={`view-hb-badge ${d.is_deleted ? "view-hb-badge-red" : "view-hb-badge-green"}`}>
              {d.is_deleted ? "Deleted" : "Active"}
            </span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><CalendarOutlined /> Created</span>
            <span className="view-hb-info-value">{formatDate(d.created_at)}</span>
          </div>

          <div className="view-hb-info-item">
            <span className="view-hb-info-label"><CalendarOutlined /> Updated</span>
            <span className="view-hb-info-value">{formatDate(d.updated_at)}</span>
          </div>

        </div>
      </div>

      {/* ── Type Data ── */}
      {td._id && (
        <div className="view-hb-card" style={{ marginTop: 14 }}>
          <h3 className="view-hb-card-header">Linked {capitalize(d.type)}</h3>
          <div className="view-hb-linked-row">
            {td.cover_photo && (
              <img src={td.cover_photo} alt="cover" className="view-hb-linked-thumb" />
            )}
            <div className="view-hb-linked-info">
              {td.project_name && <p className="view-hb-linked-name">{td.project_name}</p>}
              {td.event_name && <p className="view-hb-linked-name">{td.event_name}</p>}
              {td.title && <p className="view-hb-linked-name">{td.title}</p>}
              {td.brand_name && (
                <p className="view-hb-linked-meta">
                  <span className="view-hb-meta-label">Brand</span> {td.brand_name}
                </p>
              )}
              {td.construction_status && (
                <p className="view-hb-linked-meta">
                  <span className="view-hb-meta-label">Status</span>
                  <span className="view-hb-badge view-hb-badge-teal" style={{ marginLeft: 6 }}>
                    {td.construction_status}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Media ── */}
      <div className="view-hb-card" style={{ marginTop: 14 }}>
        <h3 className="view-hb-card-header">Media</h3>

        <div className="view-hb-media-grid">
          {/* Main media */}
          <div className="view-hb-media-block">
            <p className="view-hb-media-label">{hasVideo ? "Video" : "Image"}</p>
            {hasVideo ? (
              <video
                src={d.home_videos}
                controls
                className="view-hb-video-player"
              />
            ) : hasImage ? (
              <img src={d.home_image} alt="Banner" className="view-hb-media-img" />
            ) : (
              <div className="view-hb-no-media">
                <PictureOutlined style={{ fontSize: 36, color: "#bbb" }} />
                <p>No media</p>
              </div>
            )}
          </div>

          {/* Thumbnail */}
          <div className="view-hb-media-block">
            <p className="view-hb-media-label">Thumbnail</p>
            {hasThumbnail ? (
              <img src={d.thumbnail} alt="Thumbnail" className="view-hb-media-img" />
            ) : (
              <div className="view-hb-no-media">
                <PictureOutlined style={{ fontSize: 36, color: "#bbb" }} />
                <p>No thumbnail</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="view-hb-actions">
        <Button size="large" onClick={onClose} className="view-hb-btn-close">
          Close
        </Button>
      </div>

    </Modal>
  );
};

export default HomeBannerViewModal;